import { Text } from "@solomon-labs/design/components/ui";
import { useEffect, useState } from "react";
import type { FormEvent, ReactElement, ReactNode } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";

import { useOrgAuth } from "../../hooks/auth";
import {
    getOrgAuthErrorMessage,
    isOrgAuthCredentialsError,
    type OAuthConfig,
    resolveOrgAuthUserMessage
} from "../../services/orgAuthService";
import { useOrgAuthStore } from "../../stores/useOrgAuthStore";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Spinner } from "../ui/spinner";

import { GoogleMarkIcon, SsoLockIcon } from "./AuthLoginIcons";
import {
    hasOrgAuthLoginFieldErrors,
    type OrgAuthLoginFieldErrors,
    validateOrgAuthLoginForm
} from "./orgAuthLoginValidation";
import {
    AUTH_INPUT_CLASS,
    OrgAuthField,
    OrgAuthOrDivider,
    OrgAuthShell
} from "./OrgAuthShell";

const OAUTH_GOOGLE_BUTTON_CLASS =
    "bg-surface-sunken hover:bg-surface-sunken/80 disabled:bg-surface-sunken";

export type OrgAuthLoginConsoleKind = "client" | "issuer";

const LOGIN_INTRO: Record<OrgAuthLoginConsoleKind, string> = {
    client: "Sign in to access your company recipient dashboard",
    issuer: "Sign in to access your issuer dashboard"
};

const FORM_ERROR_ID = "org-auth-login-form-error";
const EMAIL_ERROR_ID = "org-auth-login-email-error";
const PASSWORD_ERROR_ID = "org-auth-login-password-error";

interface OrgAuthLoginPageProps {
    config: OAuthConfig;
    consoleKind: OrgAuthLoginConsoleKind;
    /** Used for role-specific auth error copy (e.g. NO_ORG_MEMBERSHIP). */
    appLabel: string;
    footerNote: string;
    /** Brand block override — defaults to the Solomon lockup. See OrgAuthShell. */
    logo?: ReactNode;
    /**
     * Sign-in intro copy. Defaults to the console-kind wording above —
     * overridden by co-branded consoles whose vocabulary differs (e.g. the
     * Western Union POC, where an issuer is called a Distributor).
     */
    intro?: string;
}

export function OrgAuthLoginPage({
    config,
    consoleKind,
    appLabel,
    footerNote,
    logo,
    intro
}: OrgAuthLoginPageProps): ReactElement {
    const tokens = useOrgAuthStore((s) => s.tokens);
    const { login, loginWithGoogle, loginWithSso, isLoggingIn, loginError } =
        useOrgAuth(config);
    const [searchParams] = useSearchParams();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [fieldErrors, setFieldErrors] = useState<OrgAuthLoginFieldErrors>({});
    const [hideRemoteError, setHideRemoteError] = useState(false);
    const authErrorFromQuery = getOrgAuthErrorMessage(
        searchParams.get("auth_error"),
        { appLabel }
    );
    const remoteLoginError =
        resolveOrgAuthUserMessage({ message: loginError, appLabel }) ??
        authErrorFromQuery;
    const visibleLoginError = hideRemoteError ? null : remoteLoginError;
    const hasCredentialError = isOrgAuthCredentialsError(visibleLoginError);
    const isGoogleEnabled = config.externalAuth?.google?.enabled === true;
    const googleLabel =
        config.externalAuth?.google?.label ?? "Continue with Google";
    const isSsoEnabled = config.externalAuth?.sso?.enabled === true;
    const ssoLabel = config.externalAuth?.sso?.label ?? "Continue with SSO";
    const hasExternalLogin = isGoogleEnabled || isSsoEnabled;

    useEffect(() => {
        if (remoteLoginError && !isLoggingIn) {
            setHideRemoteError(false);
        }
    }, [remoteLoginError, isLoggingIn]);

    if (tokens) {
        return <Navigate to="/" replace />;
    }

    const handleSubmit = (e: FormEvent): void => {
        e.preventDefault();
        if (isLoggingIn) {
            return;
        }

        const { fieldErrors: nextFieldErrors, normalizedEmail } =
            validateOrgAuthLoginForm(email, password);
        setFieldErrors(nextFieldErrors);

        if (hasOrgAuthLoginFieldErrors(nextFieldErrors)) {
            setHideRemoteError(true);
            return;
        }

        setHideRemoteError(true);
        void login(normalizedEmail, password).catch(() => undefined);
    };

    const handleGoogleLogin = (): void => {
        const loginHint = email.trim() || undefined;
        setFieldErrors({});
        setHideRemoteError(true);
        void loginWithGoogle(loginHint).catch(() => undefined);
    };

    const handleSsoLogin = (): void => {
        const loginHint = email.trim() || undefined;
        setFieldErrors({});
        setHideRemoteError(true);
        void loginWithSso(loginHint).catch(() => undefined);
    };

    const handleEmailChange = (nextEmail: string): void => {
        setEmail(nextEmail);
        setFieldErrors((current) => ({ ...current, email: undefined }));
        if (remoteLoginError) {
            setHideRemoteError(true);
        }
    };

    const handlePasswordChange = (nextPassword: string): void => {
        setPassword(nextPassword);
        setFieldErrors((current) => ({ ...current, password: undefined }));
        if (remoteLoginError) {
            setHideRemoteError(true);
        }
    };

    const emailDescribedBy =
        [
            fieldErrors.email ? EMAIL_ERROR_ID : null,
            hasCredentialError ? FORM_ERROR_ID : null
        ]
            .filter(Boolean)
            .join(" ") || undefined;
    const passwordDescribedBy =
        [
            fieldErrors.password ? PASSWORD_ERROR_ID : null,
            hasCredentialError ? FORM_ERROR_ID : null
        ]
            .filter(Boolean)
            .join(" ") || undefined;

    const oauthButtonContent = (
        icon: ReactNode,
        label: string
    ): ReactElement => (
        <span className="flex w-full items-center justify-center gap-2">
            {icon}
            {label}
        </span>
    );

    return (
        <OrgAuthShell
            intro={intro ?? LOGIN_INTRO[consoleKind]}
            footerNote={footerNote}
            logo={logo}
        >
            <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-6"
                noValidate
            >
                {hasExternalLogin && (
                    <div className="flex flex-col gap-3">
                        {isGoogleEnabled && (
                            <Button
                                type="button"
                                variant="inverse-raised"
                                interaction="default"
                                size="md"
                                outline
                                fullWidth
                                disabled={isLoggingIn}
                                className={OAUTH_GOOGLE_BUTTON_CLASS}
                                onClick={handleGoogleLogin}
                            >
                                {oauthButtonContent(
                                    <GoogleMarkIcon />,
                                    googleLabel
                                )}
                            </Button>
                        )}

                        {isSsoEnabled && (
                            <Button
                                type="button"
                                variant="inverse-raised"
                                interaction="default"
                                size="md"
                                outline
                                fullWidth
                                disabled={isLoggingIn}
                                onClick={handleSsoLogin}
                            >
                                {oauthButtonContent(
                                    <SsoLockIcon className="text-foreground-base" />,
                                    ssoLabel
                                )}
                            </Button>
                        )}
                    </div>
                )}

                {hasExternalLogin && <OrgAuthOrDivider />}

                <div className="flex flex-col gap-4">
                    <OrgAuthField id="email" label="Email">
                        <Input
                            id="email"
                            type="email"
                            autoComplete="email"
                            inputMode="email"
                            autoCapitalize="none"
                            spellCheck={false}
                            required
                            value={email}
                            onChange={(e) => handleEmailChange(e.target.value)}
                            onBlur={() => setEmail((value) => value.trim())}
                            surface="filled"
                            size="md"
                            state={
                                fieldErrors.email || hasCredentialError
                                    ? "error"
                                    : undefined
                            }
                            aria-describedby={emailDescribedBy}
                            placeholder="you@example.com"
                            disabled={isLoggingIn}
                            className={AUTH_INPUT_CLASS}
                        />
                        {fieldErrors.email && (
                            <Text
                                id={EMAIL_ERROR_ID}
                                variant="fine-print"
                                role="alert"
                                className="text-error-base text-pretty"
                            >
                                {fieldErrors.email}
                            </Text>
                        )}
                    </OrgAuthField>

                    <OrgAuthField
                        id="password"
                        label="Password"
                        labelEnd={
                            <Text
                                variant="fine-print"
                                className="text-foreground-muted underline-offset-2 hover:text-foreground-base hover:underline"
                                render={<Link to="/forgot-password" />}
                            >
                                Forgot your password?
                            </Text>
                        }
                    >
                        <Input
                            id="password"
                            type="password"
                            autoComplete="current-password"
                            required
                            value={password}
                            onChange={(e) =>
                                handlePasswordChange(e.target.value)
                            }
                            surface="filled"
                            size="md"
                            state={
                                fieldErrors.password || hasCredentialError
                                    ? "error"
                                    : undefined
                            }
                            aria-describedby={passwordDescribedBy}
                            placeholder="Enter your password"
                            disabled={isLoggingIn}
                            className={AUTH_INPUT_CLASS}
                        />
                        {fieldErrors.password && (
                            <Text
                                id={PASSWORD_ERROR_ID}
                                variant="fine-print"
                                role="alert"
                                className="text-error-base text-pretty"
                            >
                                {fieldErrors.password}
                            </Text>
                        )}
                    </OrgAuthField>

                    {visibleLoginError && (
                        <Text
                            id={FORM_ERROR_ID}
                            variant="small"
                            role="alert"
                            aria-live="polite"
                            className="text-error-base text-pretty"
                        >
                            {visibleLoginError}
                        </Text>
                    )}
                </div>

                <Button
                    type="submit"
                    variant="raised"
                    interaction="lifted"
                    size="md"
                    fullWidth
                    disabled={isLoggingIn}
                >
                    {isLoggingIn ? (
                        <span className="flex items-center justify-center gap-2">
                            <Spinner surface="dark" className="size-3.5" />
                            Signing in
                        </span>
                    ) : (
                        "Sign in"
                    )}
                </Button>
            </form>
        </OrgAuthShell>
    );
}
